<script setup lang="ts">
import { formatAmount } from '@/utils/formatters';
import { computed, inject, onMounted, reactive, ref, toRaw } from 'vue';
import { useRouter } from 'vue-router';
import { useMarketCartStore } from '@/stores/cart.store';
import { showAlert } from '@/utils/swal';

const BACKEND_URL = inject('BACKEND_URL');
const router = useRouter();
const cart = useMarketCartStore();
const layout = ref('none');
const loadingCart = ref(false);
const startupInfos = reactive({
    product: {} as any,
    market_echeances_time: -1,
});
const echeancesPaiement = ref([] as any[]);
const formGroup = reactive({
    mode_paiement: null as string | null,
});

onMounted(() => {
  layout.value = router.currentRoute.value.meta.layout as string || 'none';
  Object.assign(startupInfos, router.currentRoute.value.meta.startup_infos);
  let echeancesOptions: any[] = [];
  if (startupInfos.product.mode_paiement == "echelonne") {
    echeancesOptions = [
        {
            value: "echelonne",
            label: `Payer en ${startupInfos.market_echeances_time} fois`,
            description: `${formatAmount(startupInfos.product.montant_echeance)} F CFA x ${startupInfos.market_echeances_time}`,
            montant: startupInfos.product.montant_apres_remises,
        }
    ];
    formGroup.mode_paiement = "echelonne";
  } else if (startupInfos.product.mode_paiement == "mixed") {
    echeancesOptions = [
        {
            value: "comptant",
            label: 'Payer en une fois',
            description: 'Avec le solde de votre wallet',
            montant: startupInfos.product.montant_apres_remises,
        },
        {
            value: "echelonne",
            label: `Payer en ${startupInfos.market_echeances_time} fois`,
            description: `${formatAmount(startupInfos.product.montant_echeance)} F CFA x ${startupInfos.market_echeances_time}`,
            montant: startupInfos.product.montant_apres_remises,
        }
    ];
    formGroup.mode_paiement = "comptant";
  } else {
    echeancesOptions = [
        {
            value: "comptant",
            label: 'Payer en une fois',
            description: 'Avec le solde de votre wallet',
            montant: startupInfos.product.montant_apres_remises,
        }
    ];
    formGroup.mode_paiement = "comptant";
  }
  echeancesPaiement.value = echeancesOptions;
});

async function addToCart() {
    if (loadingCart.value) { return; }

    loadingCart.value = true;
    try {
        if (formGroup.mode_paiement === 'comptant') {
            await cart.addToCart({
                id: startupInfos.product.id,
                nom: startupInfos.product.nom,
                image: startupInfos.product.image,
                montant: startupInfos.product.montant,
                type_promotion: startupInfos.product.type_promotion,
                taux_promotion: startupInfos.product.taux_promotion,
                valeur_promotion: startupInfos.product.valeur_promotion,
                montant_apres_remises: startupInfos.product.montant_apres_remises,
                mode_paiement: formGroup.mode_paiement,
                montant_echeance: 0,
                nb_echeance: 0,
                total_promotion: 0,
                sous_total: 0,
                total: 0,
                total_echeance: 0,
            });
        } else {
            const echeance = toRaw(echeancesPaiement.value).find(e => e.value === formGroup.mode_paiement);
            if (echeance) {
                await cart.addToCart({
                    id: startupInfos.product.id,
                    nom: startupInfos.product.nom,
                    image: startupInfos.product.image,
                    montant: startupInfos.product.montant,
                    type_promotion: startupInfos.product.type_promotion,
                    taux_promotion: startupInfos.product.taux_promotion,
                    valeur_promotion: startupInfos.product.valeur_promotion,
                    montant_apres_remises: startupInfos.product.montant_apres_remises,
                    mode_paiement: formGroup.mode_paiement!,
                    montant_echeance: startupInfos.product.montant_echeance,
                    nb_echeance: startupInfos.market_echeances_time,
                    total_promotion: 0,
                    sous_total: 0,
                    total: 0,
                    total_echeance: 0,
                });
            }
        }
    } catch(error: any) {
        await showAlert.error(typeof error === 'string' ? error : 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
        loadingCart.value = false;
    }
}

</script>
<template>
    <main>
        <div class="produit-area">
            <div class="page-space-50" v-if="layout == 'none'"></div>
            <section class="container-small">
                <div class="s10_mobile position-relative" style="height: 380px; display: flex; justify-content: center; align-items: center;">
                    <div class="tag-set justify-content-left" style="top: 0;" v-if="startupInfos.product.type_promotion == 'PERCENTAGE' && startupInfos.product.taux_promotion">
                        <div class="tag2">-{{ Math.round(startupInfos.product.taux_promotion) }}%</div>
                        <!-- <div class="tag-promo">Promo !</div> -->
                    </div>
                    <img :src="BACKEND_URL + '/assets/market_produits/1000/' + startupInfos.product.image" style="max-width: 100%; max-height: 350px;" />
                </div>
                <div class="s1_mobile">&nbsp;</div>
                <div class="s9_mobile">
                    <h1 class="secondary-font mt-5 mb-6">
                        {{ startupInfos.product.nom }}
                    </h1>
                    <h1 class="text-4xl secondary-font mb-5">
                        {{ formatAmount(startupInfos.product.montant_apres_remises) }} F CFA
                    </h1>
                    <div class="h-0"></div>

                    <div class="card-check bg-white-50 widget flex my-2 pt-6 pb-5 px-6" :class="{'active': formGroup.mode_paiement === echeance.value}" v-for="(echeance, index) of echeancesPaiement" :key="index">
                        <label class="s20" :for="'echeance_' + index">
                            <div class="s1 mt-1"><input class="" type="radio" name="echeance" :id="'echeance_' + index" :value="echeance.value" v-model="formGroup.mode_paiement" /></div>
                            <h5 class="s14 pl-2 secondary-font">{{ echeance.label }} <br class="mb-3"/>
                                <span class="secondary-font text-grey-600">{{ echeance.description }}</span>
                            </h5>
                            <h4 class="s5 text-right secondary-font">{{ formatAmount(echeance.montant) }} F</h4>
                        </label>
                    </div>
                    <!-- <div class="card-check bg-white-50 widget flex my-2 pt-6 pb-5 px-6">
                        <label class="s20" for="super_1">
                            <div class="s1 mt-1"><input class="" type="radio" name="super" id="super_1" value="1" /></div>
                            <h5 class="s14 pl-2 secondary-font">Payer en plusieurs fois<br class="mb-3"/>
                                <span class="secondary-font text-grey-600">225 000 F CFA x 4</span>
                            </h5>
                            <h4 class="s5 text-right secondary-font">225 000 F <br>/ mois</h4>
                        </label>
                    </div> -->

                    <div class="clear-both h-20"></div>

                    <button class="btn-ultra lg w-100" :class="{'loading': loadingCart}" @click="addToCart" :disabled="loadingCart"><!-- loading sm tips color2 light white white2 success info warning danger-->
                        <div class="btn-show">
                            <img src="/public/icons/icon-panier.svg" class="btn-icon" alt="" />
                            <span>&nbsp;Ajouter au panier</span>
                        </div>
                        <div class="btn-loading">
                            <div class="loader-dots"></div>
                        </div>
                    </button>
                    <div class="h-30"></div>
                </div>
                <div class="clear-both h-10"></div>

                <div class="flex">
                    <p class="s10_mobile text-read" v-html="startupInfos.product.description">
                    </p>
                    <div class="s1_mobile">&nbsp;</div>
                    <div class="s9_mobile ">
                        
                    </div>
                </div>

            </section>

            <div class="clear-both h-60"></div>
        </div>
    </main>
</template>